/**
 * MamaLink - Helpers pour Push Notifications
 */
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Convertit la clé VAPID base64url en Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export const requestNotificationPermission = async () => {
  if (!('Notification' in window)) {
    console.log('Notifications not supported');
    return false;
  }
  
  if (Notification.permission === 'granted') {
    return true;
  }
  
  if (Notification.permission !== 'denied') {
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  }
  
  return false;
};

export const registerServiceWorker = async () => {
  if ('serviceWorker' in navigator) {
    try {
      // Register service worker
      const registration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/',
        updateViaCache: 'none' // Force check for updates
      });
      
      // Check for updates immediately
      registration.update();
      
      // Wait for the service worker to be ready
      await navigator.serviceWorker.ready;
      
      // Listen for update found
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (newWorker) {
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              // New version available - notify user
              console.log('[SW] New version available');
              toast.info('Une mise à jour est disponible ! Rafraîchis la page.', {
                duration: 10000,
                action: {
                  label: 'Rafraîchir',
                  onClick: () => window.location.reload()
                }
              });
            }
          });
        }
      });
      
      // Listen for SW messages
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data && event.data.type === 'SW_UPDATED') {
          console.log('[SW] Service worker updated to version:', event.data.version);
          // Auto-reload when SW updates
          window.location.reload();
        }
      });
      
      console.log('Service Worker registered and ready:', registration);
      return registration;
    } catch (error) {
      console.error('Service Worker registration failed:', error);
      return null;
    }
  }
  return null;
};

export const subscribeToNotifications = async (token, preferences, options = {}) => {
  // 🎯 Élodie #1073 — Mode silencieux pour les re-souscriptions au boot :
  // aucun toast d'erreur ne s'affiche, seuls les logs partent en console.
  // Le mode bruyant (par défaut) est utilisé quand la maman clique elle-même
  // sur "Activer les notifications" depuis les paramètres.
  const silent = options.silent === true;
  const notify = (fn, msg) => { if (!silent) fn(msg); };

  // 📱 Élodie — Dans la coquille native (app iOS/Android), le WebView n'implémente
  // jamais serviceWorker/PushManager : ce n'est pas un navigateur non supporté,
  // c'est structurel. Les notifications y sont déjà enregistrées automatiquement
  // par l'app native au login (canal séparé, indépendant de ce toggle web) — on
  // se contente donc d'enregistrer la préférence, sans tenter le flux Web Push.
  if (window.isNativeApp) {
    try {
      await axios.post(`${API}/api/notifications/preferences`, { ...preferences, enabled: true }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Error saving notification preference (native app):', error);
    }
    return true;
  }

  // 1. Vérifier le support
  if (!('PushManager' in window) || !('serviceWorker' in navigator)) {
    notify(toast.error, "Ton navigateur ne supporte pas les notifications push.");
    return false;
  }

  // 2. Demander la permission
  // 🎯 Élodie #1150 — En mode silencieux (re-subscribe automatique au boot),
  //    on ne déclenche JAMAIS le prompt navigateur. Si la permission n'est pas
  //    déjà 'granted', on abandonne silencieusement — la maman devra cliquer
  //    explicitement sur "Activer les notifications" pour déclencher le prompt.
  if (silent) {
    if (!('Notification' in window) || Notification.permission !== 'granted') {
      return false;
    }
  } else {
    const permission = await requestNotificationPermission();
    if (!permission) {
      notify(toast.error, 'Les notifications sont bloquées dans ton navigateur. Autorise-les dans les paramètres.');
      return false;
    }
  }

  // 3. Enregistrer le service worker
  const registration = await registerServiceWorker();
  if (!registration) {
    notify(toast.error, "Impossible d'activer les notifications");
    return false;
  }

  try {
    // 4. Créer (ou récupérer) l'abonnement Push réel
    const vapidPublicKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
    if (!vapidPublicKey) {
      console.error('VAPID public key missing in REACT_APP_VAPID_PUBLIC_KEY');
      notify(toast.error, "Configuration push manquante. Contacte le support.");
      return false;
    }

    let pushSubscription = await registration.pushManager.getSubscription();
    if (!pushSubscription) {
      pushSubscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
      });
    }

    // 5. Envoyer l'abonnement au backend (CRITIQUE pour recevoir des push)
    // Le backend attend la subscription FLAT (endpoint + keys directement à la racine)
    await axios.post(
      `${API}/api/users/me/push-subscription`,
      pushSubscription.toJSON(),
      { headers: { Authorization: `Bearer ${token}` } }
    );

    // 6. Sauvegarder les préférences
    const prefsToSave = { ...preferences, enabled: true };
    await axios.post(`${API}/api/notifications/preferences`, prefsToSave, {
      headers: { Authorization: `Bearer ${token}` }
    });

    localStorage.setItem('mamalink_notifs_enabled', 'true');
    // 🎯 Élodie #1001 — Retrait du toast "Notifications activées..." qui apparaissait
    // silencieusement à chaque connexion (subscribeToNotifications appelé au boot).
    // Rien ne doit s'afficher en éphémère au login.
    return true;
  } catch (error) {
    console.error('Error subscribing:', error);
    if (error?.name === 'NotAllowedError') {
      notify(toast.error, 'Les notifications sont bloquées. Autorise-les dans les paramètres du navigateur.');
    } else {
      notify(toast.error, "Erreur lors de l'activation des notifications");
    }
    return false;
  }
};

// Send a local notification (for testing/demo)
export const sendLocalNotification = (title, body, type = 'general') => {
  if (Notification.permission === 'granted') {
    new Notification(title, {
      body,
      icon: '/icon-192x192.png',
      tag: 'mamalink-' + type,
      data: { type }
    });
  }
};
